import {
  Close as CloseIcon,
  ExpandLess as CollapseIcon,
  Delete as DeleteIcon,
  NotificationsNone as EmptyIcon,
  ExpandMore as ExpandIcon,
  FilterList as FilterIcon,
  MoreVert as MoreIcon,
  CheckCircle as ReadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { isNotificationRead } from "../../utils/notifications/notificationRead";
import TemplatesManager from "./TemplatesManager";
import CategoriesManager from "./CategoriesManager";
import BulkNotificationManager from "./BulkNotificationManager";
import { useTranslation } from "react-i18next";
import { useNotificationText } from "../../hooks/useNotificationText";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../../hooks/useNotifications";
import { resolveNotificationLink } from "../../utils/notifications/notificationLink";
import {
  INotification,
  NotificationPriority,
  NotificationType,
} from "../../types/notifications";
import PushNotificationManager from "./PushNotificationManager";

/**
 * NotificationPanel - Выпадающая панель уведомлений для desktop
 * Фаза 2: Система уведомлений (Неделя 8: UI компоненты уведомлений)
 */

interface NotificationPanelProps {
  open: boolean;
  onClose: () => void;
  anchorEl?: HTMLElement | null;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  open,
  onClose,
  anchorEl,
}) => {
  const { t } = useTranslation("notifications");
  // AD-378: на экране — перевод по ключам из `data`, а не русский текст,
  // который сервер положил в строку уведомления.
  const textOf = useNotificationText();
  const navigate = useNavigate();
  const {
    notifications,
    groups,
    unreadCount,
    isLoading,
    filters,
    selectedNotifications,
    hasSelectedNotifications,
    loadNotifications,
    loadMoreNotifications,
    markNotificationsAsRead,
    markAllNotificationsAsRead,
    deleteSelectedNotifications,
    updateFilters,
    updateSearchQuery,
    resetFilters,
    toggleSelection,
    selectAll,
    clearSelections,
    notificationsByType,
    unreadByType,
  } = useNotifications();

  // Локальное состояние
  const [searchQuery, setSearchQuery] = useState(filters.search || "");
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  // AD-185: стор наполнялся только по «Refresh» — панель открывалась пустой при ненулевом
  // счётчике колокольчика (тот считается отдельным запросом `/unread-count`).
  useEffect(() => {
    if (open) {
      void loadNotifications();
    }
  }, [open, loadNotifications]);

  // Обработчики событий
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const query = event.target.value;
      setSearchQuery(query);
      updateSearchQuery(query);
    },
    [updateSearchQuery],
  );

  const handleFilterClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setFilterMenuAnchor(event.currentTarget);
    },
    [],
  );

  const handleMoreClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setMoreMenuAnchor(event.currentTarget);
    },
    [],
  );

  const handleFilterClose = useCallback(() => {
    setFilterMenuAnchor(null);
  }, []);

  const handleMoreClose = useCallback(() => {
    setMoreMenuAnchor(null);
  }, []);

  const handleTypeFilter = useCallback(
    (type: NotificationType) => {
      const currentTypes = filters.types || [];
      const newTypes = currentTypes.includes(type)
        ? currentTypes.filter((t) => t !== type)
        : [...currentTypes, type];

      updateFilters({ types: newTypes });
      handleFilterClose();
    },
    [filters.types, updateFilters, handleFilterClose],
  );

  const handlePriorityFilter = useCallback(
    (priority: NotificationPriority) => {
      const currentPriorities = filters.priority || [];
      const newPriorities = currentPriorities.includes(priority)
        ? currentPriorities.filter((p) => p !== priority)
        : [...currentPriorities, priority];

      updateFilters({ priority: newPriorities });
      handleFilterClose();
    },
    [filters.priority, updateFilters, handleFilterClose],
  );

  const handleMarkAllRead = useCallback(async () => {
    await markAllNotificationsAsRead();
    handleMoreClose();
  }, [markAllNotificationsAsRead, handleMoreClose]);

  const handleDeleteSelected = useCallback(async () => {
    if (hasSelectedNotifications) {
      await deleteSelectedNotifications(selectedNotifications);
      clearSelections();
    }
    handleMoreClose();
  }, [
    hasSelectedNotifications,
    deleteSelectedNotifications,
    selectedNotifications,
    clearSelections,
    handleMoreClose,
  ]);

  const handleRefresh = useCallback(async () => {
    await loadNotifications();
    handleMoreClose();
  }, [loadNotifications, handleMoreClose]);

  const handleToggleGroup = useCallback(
    (groupId: string) => {
      const newExpanded = new Set(expandedGroups);
      if (newExpanded.has(groupId)) {
        newExpanded.delete(groupId);
      } else {
        newExpanded.add(groupId);
      }
      setExpandedGroups(newExpanded);
    },
    [expandedGroups],
  );

  const handleNotificationClick = useCallback(
    (notification: INotification) => {
      if (!isNotificationRead(notification)) {
        markNotificationsAsRead([notification.id]);
      }

      // AD-150. Ссылка лежит в `data.linkUrl` — см. resolveNotificationLink.
      // Переход внутренний: раньше здесь стоял window.open(..., "_blank"),
      // который на внутреннем пути открывал ВТОРУЮ вкладку и перезагружал
      // одностраничное приложение целиком, теряя состояние.
      const link = resolveNotificationLink(notification);
      if (link) {
        onClose();
        navigate(link);
      }
    },
    [markNotificationsAsRead, navigate, onClose],
  );

  // Фильтрованные уведомления
  const filteredNotifications = useMemo(() => {
    let result = notifications;

    if (showOnlyUnread) {
      result = result.filter((n) => !isNotificationRead(n));
    }

    return result;
  }, [notifications, showOnlyUnread]);

  // Группированные уведомления
  const groupedNotifications = useMemo(() => {
    if (!filters.types?.length && !showOnlyUnread) {
      return groups;
    }

    // Если есть фильтры, группируем заново
    const grouped: { [key: string]: INotification[] } = {};

    filteredNotifications.forEach((notification) => {
      const key = notification.type;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(notification);
    });

    return Object.entries(grouped).map(([type, notifications]) => ({
      id: `group-${type}`,
      type: type as NotificationType,
      count: notifications.length,
      latestNotification: notifications[0],
      notifications,
      isExpanded: expandedGroups.has(`group-${type}`),
    }));
  }, [
    groups,
    filters.types,
    showOnlyUnread,
    filteredNotifications,
    expandedGroups,
  ]);

  // Получение иконки для типа уведомления
  const getNotificationIcon = useCallback((type: NotificationType) => {
    const iconMap: Partial<Record<NotificationType, string>> = {
      [NotificationType.SYSTEM]: "⚙️",
      [NotificationType.EVENT]: "📅",
      [NotificationType.TASK]: "✅",
      [NotificationType.USER]: "👤",
      [NotificationType.COMMENT]: "💬",
      [NotificationType.REFERRAL]: "🎁",
      [NotificationType.ACHIEVEMENT]: "🏆",
      [NotificationType.PROMOTION]: "🎉",
      [NotificationType.REMINDER]: "⏰",
      [NotificationType.SOCIAL]: "👥",
      [NotificationType.SECURITY]: "🔒",
    };
    return iconMap[type] || "📢";
  }, []);

  // Получение цвета для приоритета
  const getPriorityColor = useCallback((priority: NotificationPriority) => {
    const colorMap: Partial<
      Record<NotificationPriority, "error" | "warning" | "info">
    > = {
      [NotificationPriority.HIGH]: "error",
      [NotificationPriority.MEDIUM]: "warning",
      [NotificationPriority.LOW]: "info",
      [NotificationPriority.URGENT]: "error",
      [NotificationPriority.CRITICAL]: "error",
    };
    return colorMap[priority] || "info";
  }, []);

  if (!open) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        position: "absolute",
        top: anchorEl ? 56 : 64,
        right: 16,
        width: 420,
        maxHeight: 600,
        display: "flex",
        flexDirection: "column",
        zIndex: 1300,
        borderRadius: 2,
      }}
    >
      {/* Заголовок */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" component="h2">
          {t("title")}
          {unreadCount > 0 && (
            <Badge badgeContent={unreadCount} color="error" sx={{ ml: 1 }} />
          )}
        </Typography>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title={t("actions.filters")}>
            <IconButton size="small" onClick={handleFilterClick}>
              <FilterIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={t("actions.more")}>
            <IconButton size="small" onClick={handleMoreClick}>
              <MoreIcon />
            </IconButton>
          </Tooltip>

          <Button size="small" onClick={() => setTemplatesOpen(true)}>
            Templates
          </Button>
          <Button size="small" onClick={() => setCategoriesOpen(true)}>
            Categories
          </Button>
          {/* TD-6 B2 (KNOWN_LIMITATIONS TD-15): массовая отправка отложена —
              бэкенд отдаёт честный 501, кнопка задизейблена до реализации. */}
          <Button
            size="small"
            variant="outlined"
            onClick={() => setBulkOpen(true)}
            disabled
            title="Массовая отправка — в разработке"
          >
            Bulk
          </Button>

          <Tooltip title={t("actions.close")}>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Поиск */}
      <Box sx={{ p: 2, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t("searchPlaceholder")}
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Push-уведомления */}
      <Box sx={{ px: 2, pb: 1 }}>
        <PushNotificationManager compact />
      </Box>

      {/* Активные фильтры */}
      {(filters.types?.length ||
        filters.priority?.length ||
        showOnlyUnread) && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {showOnlyUnread && (
              <Chip
                label={t("filters.onlyUnread")}
                size="small"
                onDelete={() => setShowOnlyUnread(false)}
              />
            )}
            {filters.types?.map((type) => (
              <Chip
                key={type}
                label={t("filters.type", { type })}
                size="small"
                onDelete={() => handleTypeFilter(type)}
              />
            ))}
            {filters.priority?.map((priority) => (
              <Chip
                key={priority}
                label={t("filters.priority", { priority })}
                size="small"
                color={getPriorityColor(priority)}
                onDelete={() => handlePriorityFilter(priority)}
              />
            ))}
            <Button
              size="small"
              onClick={resetFilters}
              sx={{ minWidth: "auto", p: 0.5 }}
            >
              {t("actions.reset")}
            </Button>
          </Box>
        </Box>
      )}

      {/* Массовые действия */}
      {hasSelectedNotifications && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Alert
            severity="info"
            action={
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  startIcon={<ReadIcon />}
                  onClick={() => markNotificationsAsRead(selectedNotifications)}
                >
                  {t("actions.markAsRead")}
                </Button>
                <Button
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteSelected}
                  color="error"
                >
                  {t("actions.delete")}
                </Button>
              </Box>
            }
          >
            {t("filters.selected", { count: selectedNotifications.length })}
          </Alert>
        </Box>
      )}

      {/* Список уведомлений */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {isLoading && filteredNotifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary">
              {t("loadingNotifications")}
            </Typography>
          </Box>
        ) : filteredNotifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <EmptyIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
            <Typography color="text.secondary">
              {t("noNotifications")}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {groupedNotifications.map((group) => (
              <React.Fragment key={group.id}>
                {/* Заголовок группы */}
                <ListItem
                  button
                  onClick={() => handleToggleGroup(group.id)}
                  sx={{
                    bgcolor: "action.hover",
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: "primary.main" }}>
                      {getNotificationIcon(group.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="subtitle2">
                          {t(`types.${group.type}`, {
                            defaultValue: group.type,
                          })}
                        </Typography>
                        <Badge
                          badgeContent={group.count}
                          color="primary"
                          max={99}
                        />
                        {unreadByType[group.type] && (
                          <Badge
                            badgeContent={unreadByType[group.type]}
                            color="error"
                            max={99}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      group.latestNotification != null
                        ? `${t("lastNotification")}: ${new Date(group.latestNotification.createdAt).toLocaleString()}`
                        : ""
                    }
                  />
                  <ListItemSecondaryAction>
                    {expandedGroups.has(group.id) ? (
                      <CollapseIcon />
                    ) : (
                      <ExpandIcon />
                    )}
                  </ListItemSecondaryAction>
                </ListItem>

                {/* Уведомления группы */}
                <Collapse in={expandedGroups.has(group.id)}>
                  {group.notifications.map((notification) => (
                    <ListItem
                      key={notification.id}
                      button
                      onClick={() => handleNotificationClick(notification)}
                      sx={{
                        pl: 4,
                        bgcolor: isNotificationRead(notification)
                          ? "transparent"
                          : "action.selected",
                        borderLeft: 4,
                        borderColor: isNotificationRead(notification)
                          ? "transparent"
                          : getPriorityColor(notification.priority) + ".main",
                      }}
                    >
                      <ListItemAvatar>
                        <Checkbox
                          checked={selectedNotifications.includes(
                            notification.id,
                          )}
                          onChange={() => toggleSelection(notification.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isNotificationRead(notification)
                                ? "normal"
                                : "bold",
                              color: isNotificationRead(notification)
                                ? "text.secondary"
                                : "text.primary",
                            }}
                          >
                            {textOf(notification).title}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {textOf(notification).content}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                            >
                              {new Date(
                                notification.createdAt,
                              ).toLocaleString()}
                            </Typography>
                          </Box>
                        }
                      />
                      {!isNotificationRead(notification) && (
                        <ListItemSecondaryAction>
                          {/* AD-151: устойчивый признак для пробы — иначе
                              «непрочитано» пришлось бы сверять по цвету фона,
                              то есть по теме, а не по решению компонента. */}
                          <Box
                            data-testid="unread-dot"
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: "primary.main",
                            }}
                          />
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                  ))}
                </Collapse>
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {/* Подвал с действиями */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          size="small"
          onClick={loadMoreNotifications}
          disabled={isLoading}
        >
          {t("actions.loadMore")}
        </Button>

        <Button
          size="small"
          onClick={selectAll}
          disabled={filteredNotifications.length === 0}
        >
          {t("actions.selectAll")}
        </Button>
      </Box>

      {/* Меню фильтров */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={handleFilterClose}
      >
        <MenuItem onClick={() => setShowOnlyUnread(!showOnlyUnread)}>
          <Checkbox checked={showOnlyUnread} />
          {t("filters.onlyUnread")}
        </MenuItem>
        <Divider />
        <MenuItem disabled>
          <Typography variant="caption">
            {t("filters.notificationTypes")}
          </Typography>
        </MenuItem>
        {Object.values(NotificationType).map((type) => (
          <MenuItem key={type} onClick={() => handleTypeFilter(type)}>
            <Checkbox checked={filters.types?.includes(type) || false} />
            {getNotificationIcon(type)} {type}
            {notificationsByType[type] && (
              <Badge
                badgeContent={notificationsByType[type]}
                color="primary"
                sx={{ ml: 1 }}
              />
            )}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem disabled>
          <Typography variant="caption">{t("filters.priorities")}</Typography>
        </MenuItem>
        {Object.values(NotificationPriority).map((priority) => (
          <MenuItem
            key={priority}
            onClick={() => handlePriorityFilter(priority)}
          >
            <Checkbox checked={filters.priority?.includes(priority) || false} />
            {priority}
          </MenuItem>
        ))}
      </Menu>

      {/* Меню дополнительных действий */}
      <Menu
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={handleMoreClose}
      >
        <MenuItem onClick={handleRefresh}>
          <RefreshIcon sx={{ mr: 1 }} />
          {t("actions.refresh")}
        </MenuItem>
        <MenuItem onClick={handleMarkAllRead} disabled={unreadCount === 0}>
          <ReadIcon sx={{ mr: 1 }} />
          {t("actions.markAllAsRead")}
        </MenuItem>
        <MenuItem
          onClick={clearSelections}
          disabled={!hasSelectedNotifications}
        >
          <CloseIcon sx={{ mr: 1 }} />
          {t("actions.clearSelection")}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleMoreClose}>
          <SettingsIcon sx={{ mr: 1 }} />
          {t("actions.notificationSettings")}
        </MenuItem>
      </Menu>
      <TemplatesManager
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
      />
      <CategoriesManager
        open={categoriesOpen}
        onClose={() => setCategoriesOpen(false)}
      />
      <BulkNotificationManager
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
      />
    </Paper>
  );
};

export default NotificationPanel;
